import PropTypes from 'prop-types';

/**
 * TextArea Component with Floating Label (Preline UI)
 *
 * Textarea component with floating label that moves up when the field has content.
 * Similar to Input component but for multiline text.
 *
 * @param {Object} props
 * @param {string} props.label - Floating label text
 * @param {string} props.value - Textarea value (controlled)
 * @param {Function} props.onChange - Callback when value changes (receives value directly)
 * @param {number} props.rows - Number of visible text rows (default: 3)
 * @param {string} props.error - Error message to display
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.disabled - Whether the textarea is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.name - Textarea name attribute
 * @param {string} props.id - Textarea id attribute
 */
export function TextArea({
  label,
  value,
  onChange,
  rows = 3,
  error,
  required = false,
  disabled = false,
  placeholder,
  name,
  id,
}) {
  const textareaId = id || name || label?.toLowerCase().replace(/\s+/g, '-');
  // Use custom placeholder if provided, otherwise use label for floating label CSS state
  const hasCustomPlaceholder = placeholder && placeholder !== label;
  const placeholderText = hasCustomPlaceholder ? placeholder : (label || '');

  // Base classes for textarea with floating label support
  // When custom placeholder is provided, make it visible and add more top padding for spacing
  const textareaClasses = `
    peer block w-full border-border-default rounded-xl text-sm
    ${hasCustomPlaceholder ? 'pt-7 px-4 pb-4' : 'p-4'}
    ${hasCustomPlaceholder ? '' : 'placeholder:transparent'}
    focus:border-brand focus:ring-brand
    disabled:opacity-50 disabled:pointer-events-none
    resize-none
    ${hasCustomPlaceholder ? '' : 'focus:pt-6 focus:pb-2'}
    ${hasCustomPlaceholder ? '' : '[&:not(:placeholder-shown)]:pt-6 [&:not(:placeholder-shown)]:pb-2'}
    ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
  `.trim().replace(/\s+/g, ' ');

  // Label classes for floating animation
  // When custom placeholder exists, always keep label floating (scaled and translated up)
  const labelClasses = `
    absolute top-0 start-0 p-4 text-sm truncate pointer-events-none
    transition ease-in-out duration-100 border border-transparent origin-[0_0]
    peer-disabled:opacity-50 peer-disabled:pointer-events-none
    peer-focus:scale-90 peer-focus:translate-x-0.5 peer-focus:-translate-y-1.5 peer-focus:text-muted
    peer-[:not(:placeholder-shown)]:scale-90 peer-[:not(:placeholder-shown)]:translate-x-0.5
    peer-[:not(:placeholder-shown)]:-translate-y-1.5 peer-[:not(:placeholder-shown)]:text-muted
    ${hasCustomPlaceholder ? 'scale-90 translate-x-0.5 -translate-y-1.5 text-muted' : ''}
    ${error ? 'peer-focus:text-red-500 peer-[:not(:placeholder-shown)]:text-red-500' : ''}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className="w-full">
      {/* Textarea Container */}
      <div className="relative">
        <textarea
          id={textareaId}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholderText}
          required={required}
          disabled={disabled}
          rows={rows}
          className={textareaClasses}
        />

        {/* Floating Label */}
        <label htmlFor={textareaId} className={labelClasses}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

TextArea.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  rows: PropTypes.number,
  error: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
};
